import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { ref, get, onValue } from "firebase/database";
import { firestore, database } from "../../firebaseConfig";
import { useActiveWorld } from "../../hooks/useActiveWorld";
import { getPrimaryDecisionMaker, parseDecisionMakers } from "../../shared/enums";
import { normalizeLeadNotes, getActivityConfig, getOutcomeConfig } from "../NotesActivityFields";
import DecisionMakersModal from "../DecisionMakersModal";
import ForwardWizard from "./ForwardWizard";
import MemberAvatar from "./MemberAvatar";
import useContactMailHistory from "./useContactMailHistory";
import useCountdown, { compactLeft } from "./useCountdown";
import { useOpenTasks } from "./useMyOpenTasks";
import { resolveTaskFlow } from "./taskFlowLink";
import { extractWrittenEmails } from "../notesContactSuggest";
import {
  getTaskAction,
  firstName,
  formatDue,
  formatOpenSince,
  loadTeamMembers,
  taskAssigneeRefs,
  completeTask,
  advanceLinkedFlow,
  taskBriefing,
  requestTaskAction,
  nameFromEmail,
  CONTACT_PATCH_EVENT,
} from "./taskUtils";
import "./TasksPage.css";

const DAY = 24 * 60 * 60 * 1000;

const SCOPES = [
  { id: "mine", label: "For me", icon: "fa-inbox" },
  { id: "byMe", label: "From me", icon: "fa-paper-plane" },
];

const contactPhone = (c) => c?.lead_phone || c?.lead_telephone || c?.phone || c?.telephone || "";
const contactMailAddresses = (contact) => {
  const email = contact?.lead_email || "";
  return [email, ...parseDecisionMakers(contact || {}).map(d => d?.email)].filter(Boolean);
};
const telHref = (p) => `tel:${String(p).replace(/[^\d+]/g, "")}`;
const copy = (text) => { try { navigator.clipboard?.writeText(text); } catch (_) { /* clipboard unavailable */ } };

const THREAD_MAX = 50;
const CELEBRATE_MS = 450;
const RESULT_MS = 9000;

const ago = (ts) => {
  if (!ts) return "";
  const s = formatOpenSince(ts);
  return s === "just now" ? s : `${s} ago`;
};

const toMs = (v) => (typeof v === "number" ? v : parseInt(v, 10) || null);
const domainOf = (addr) => (addr || "").split("@")[1]?.toLowerCase() || "";
const linkOf = (v) => (!v ? "" : v.startsWith("http") ? v : `https://${v}`);
const inDays = (n) => (n > 0 ? `in ${n} ${n === 1 ? "day" : "days"}` : "right away");

const MAIL_KIND = { intro: "Intro", follow_up: "Follow-up", followup: "Follow-up", reply: "Reply", forward: "Forward" };

const sortTasks = (list) => [...list].sort((a, b) => {
  const da = a.dueAt || Infinity;
  const dbb = b.dueAt || Infinity;
  return da !== dbb ? da - dbb : (a.createdAt || 0) - (b.createdAt || 0);
});

/** Notes + real mail traffic as one oldest-first conversation with the contact. */
const buildHistory = (contact, memberByUid, fallbackMember, mails) => {
  const items = normalizeLeadNotes(contact?.lead_notes).map(n => {
    const author = memberByUid(n.createdByUid)
      || (n.createdByUid || n.createdByEmail ? fallbackMember({ uid: n.createdByUid, email: n.createdByEmail }) : null);
    return {
      id: n.id,
      type: "note",
      kind: !n.activity || n.activity === "note" ? "note" : "action",
      at: toMs(n.createdAt),
      icon: getActivityConfig(n.activity).icon,
      title: [getActivityConfig(n.activity).label, getOutcomeConfig(n.outcome)?.label].filter(Boolean).join(" · "),
      text: n.text,
      author,
      tone: getOutcomeConfig(n.outcome)?.tone,
    };
  });
  mails.forEach(m => items.push({
    id: m.id,
    type: m.dir === "in" ? "mail-in" : "mail-out",
    kind: m.dir === "in" ? "reply" : "action",
    at: m.at,
    icon: m.dir === "in" ? "fa-reply" : "fa-paper-plane",
    title: m.dir === "in" ? "Replied by email" : `Email sent${m.kind && MAIL_KIND[m.kind] ? ` · ${MAIL_KIND[m.kind]}` : ""}`,
    text: m.subject,
    address: m.address,
  }));
  const last = contact?.lead_last_email;
  if (!mails.length && last?.timestamp) {
    items.push({
      id: "last_email", type: "mail-out", kind: "action", at: toMs(last.timestamp), icon: "fa-paper-plane",
      title: last.status ? `Email sent · ${last.status}` : "Email sent", text: last.subject || "", address: "",
    });
  }
  return items.sort((a, b) => (a.at || 0) - (b.at || 0));
};

const headlineFor = (action, who) => {
  const name = who || "the contact";
  if (action === "send_mail") return `Send ${name} a mail`;
  if (action === "call_back") return `Call ${name} back`;
  if (action === "book_meeting") return `Book a meeting with ${name}`;
  return `Follow up with ${name}`;
};

const dueInfo = (dueAt, left) => {
  if (!dueAt) return { text: "No deadline", tone: "" };
  if (left != null && left < 0) return { text: `${compactLeft(left)} overdue`, tone: "is-late" };
  if (left != null && left < DAY) return { text: `Due in ${compactLeft(left)}`, tone: "is-soon" };
  return { text: `Due ${formatDue(dueAt)}`, tone: "" };
};

const memberLookup = (members) => {
  const byUid = (uid) => members.find(m => m.uid === uid);
  const fallback = (r) => ({ uid: r?.uid || r?.email, name: r?.name || nameFromEmail(r?.email) || "Teammate" });
  const label = (m) => (m.isSelf ? "You" : firstName(m.name));
  return { byUid, fallback, label };
};

/** "Before → now → after" for the task, with the flow as the source of truth for "after". */
const FlowStrip = ({ sourceNote, act, link }) => {
  const before = sourceNote
    ? {
      icon: getActivityConfig(sourceNote.activity).icon,
      label: [getActivityConfig(sourceNote.activity).label, getOutcomeConfig(sourceNote.outcome)?.label].filter(Boolean).join(" · "),
    }
    : { icon: "fa-flag", label: "Task created" };

  let after;
  if (link.kind === "fulfils") {
    after = { icon: link.after.icon, brand: link.after.brand, label: link.after.channel === "end" ? "Flow ends" : `${link.after.label} ${inDays(link.after.delayDays)}`, tone: "is-flow" };
  } else if (link.kind === "extra") {
    after = {
      icon: link.step.icon,
      brand: link.step.brand,
      label: link.dueAt ? `${link.step.label} · ${formatDue(link.dueAt)}` : link.step.label,
      tone: "is-flow",
    };
  } else {
    after = { icon: "fa-circle-question", label: "Nothing planned", tone: "is-open" };
  }

  return (
    <div className="tp-flow">
      <ol className="tp-flow-steps">
        <li className="is-past">
          <span><i className="fa fa-check"></i></span>
          <div><small>Before</small><strong><i className={`fa ${before.icon}`}></i>{before.label}</strong></div>
        </li>
        <li className="is-now">
          <span><i className={`fa ${act?.icon || "fa-bolt"}`}></i></span>
          <div><small>Now</small><strong>{act?.label || "Task"}</strong></div>
        </li>
        <li className={after.tone} title={link.flowName ? `Flow: ${link.flowName}` : undefined}>
          <span><i className={`${after.brand ? "fab" : "fa"} ${after.icon}`}></i></span>
          <div><small>After</small><strong>{after.label}</strong></div>
        </li>
      </ol>
    </div>
  );
};

/**
 * Our side sits right with the teammate's avatar, the contact's replies left.
 * Notes and replies are speech bubbles; performed actions are compact event cards.
 */
const ThreadMessage = ({ item, contactInitials, label }) => {
  const theirs = item.kind === "reply";
  const who = item.type === "note"
    ? (item.author ? label(item.author) : "")
    : theirs ? item.address : item.address ? `to ${item.address}` : "";
  const meta = [who, item.at ? formatDue(item.at) : null].filter(Boolean).join(" · ");
  const avatar = theirs
    ? <span className="tp-msg-avatar is-contact">{contactInitials}</span>
    : item.author
      ? <MemberAvatar member={item.author} size={28} className="tp-chat-avatar" />
      : <span className="tp-msg-avatar"><i className={`fa ${item.icon}`}></i></span>;

  if (item.kind === "action") {
    return (
      <li className={`tp-msg is-ours is-action ${item.tone ? `is-${item.tone}` : ""}`}>
        {avatar}
        <div className="tp-event">
          <span className="tp-event-icon"><i className={`fa ${item.icon}`}></i></span>
          <span className="tp-event-body">
            <strong>{item.title}</strong>
            {item.text && <span className="tp-event-text">{item.text}</span>}
            <small>{meta}</small>
          </span>
        </div>
      </li>
    );
  }

  return (
    <li className={`tp-msg ${theirs ? "is-theirs" : "is-ours is-note"}`}>
      {avatar}
      <div className="tp-msg-bubble">
        <span className="tp-msg-kind"><i className={`fa ${item.icon}`}></i>{theirs ? item.title : "Note"}</span>
        {item.text && <p className="tp-msg-text">{item.text}</p>}
        <span className="tp-msg-meta">{meta}</span>
      </div>
    </li>
  );
};

const FocusCard = ({ worldId, task, contact, members, templates, link, onRun, onDone, canInbox, onContactPatch, position, total, mails, mailsLoading }) => {
  const [panel, setPanel] = useState(null);
  const [showWays, setShowWays] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [whoKey, setWhoKey] = useState("");
  const [askTo, setAskTo] = useState(null);
  const threadRef = useRef(null);
  const [copied, setCopied] = useState("");
  const [dmOpen, setDmOpen] = useState(false);
  const left = useCountdown(task.dueAt);
  const act = getTaskAction(task.action);
  const { byUid, fallback, label } = memberLookup(members);
  const by = byUid(task.assignedByUid) || fallback({ uid: task.assignedByUid, email: task.assignedByEmail });
  const assignees = taskAssigneeRefs(task).map(a => byUid(a.uid) || fallback(a));
  const fromName = label(by);
  const toNames = assignees.map(label).join(", ") || "the team";
  const selfTask = assignees.length === 1 && toNames === fromName;

  const email = contact?.lead_email || "";
  const phone = contactPhone(contact);
  const primaryDm = getPrimaryDecisionMaker(contact || {});
  const dms = useMemo(() => parseDecisionMakers(contact || {}).filter(d => d && (d.name || d.email || d.phone)), [contact]);
  const colleagues = dms.filter(d => d.email && d.email.toLowerCase() !== email.toLowerCase());
  const mailList = mails || [];
  const history = useMemo(() => buildHistory(contact, byUid, fallback, mailList), [contact, members, mailList]); // eslint-disable-line react-hooks/exhaustive-deps
  const thread = history.slice(-THREAD_MAX);
  const replied = contact?.lead_reply === true || mailList.some(m => m.dir === "in");
  const status = contact?.lead_contacted && contact.lead_contacted !== "Not Contacted" ? contact.lead_contacted : null;
  const website = contact?.lead_website || contact?.website || "";
  const city = contact?.lead_city || contact?.city || "";
  const domain = domainOf(email) || domainOf(dms.find(d => d.email)?.email);
  const due = dueInfo(task.dueAt, left);

  const sourceNote = task.noteId ? normalizeLeadNotes(contact?.lead_notes).find(n => n.id === task.noteId) : null;
  const selectedDm = dms.find(d => (d.email || d.name) === whoKey)
    || dms.find(d => primaryDm && ((d.email && d.email === primaryDm.email) || (!primaryDm.email && d.name === primaryDm.name)))
    || dms[0]
    || null;
  const company = task.contactName || contact?.business_name || "Contact";
  const personName = selectedDm?.name || nameFromEmail(selectedDm?.email || "") || "";
  const personFirst = personName ? firstName(personName) : "";
  const callPhone = selectedDm?.phone || phone;
  const callFirst = task.action === "call_back" && !!callPhone;
  const personEmail = selectedDm?.email || "";
  const personRole = selectedDm?.position || selectedDm?.role || "";
  const linkedin = selectedDm?.socials?.linkedin || selectedDm?.linkedin || "";
  const otherMail = personEmail && email && personEmail.toLowerCase() !== email.toLowerCase() ? email : "";
  const place = [city, domain].filter(Boolean).join(" · ");

  const brief = (task.message || "").trim();
  const hasBrief = !!brief && brief !== act?.label;
  const headline = headlineFor(task.action, personFirst || company);
  const recipient = personName ? `${personName}${personRole ? `, ${personRole}` : ""}` : company;
  const aiBrief = taskBriefing(task, by.name);
  const contactInitials = company.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join("");

  useLayoutEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length]);

  useEffect(() => {
    if (panel !== "template" && panel !== "scratch") return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : !e.target.closest?.(".tp-tool-slot")) setPanel(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [panel]);

  const toggle = (id) => setPanel(p => (p === id ? null : id));
  const copyKey = (key, text) => {
    copy(text);
    setCopied(key);
    window.setTimeout(() => setCopied(current => (current === key ? "" : current)), 1200);
  };

  const done = async () => {
    if (phase !== "idle") return;
    setPhase("celebrate");
    await new Promise(r => setTimeout(r, CELEBRATE_MS));
    try {
      await onDone();
    } catch (err) {
      console.error("Error completing task:", err);
      setPhase("idle");
    }
  };

  const defaultTo = (personEmail || email || "").toLowerCase();
  const mentioned = useMemo(
    () => extractWrittenEmails([task.message, sourceNote?.text].filter(Boolean).join("\n")).filter(e => e !== defaultTo),
    [task.message, sourceNote?.text, defaultTo],
  );

  const mailRun = (extra = {}, recipient) => {
    if (recipient === undefined && mentioned.length) {
      setAskTo(extra);
      return;
    }
    onRun({ ...extra, taskId: task.id, brief: aiBrief, ...(recipient ? { recipient } : {}) });
  };
  const chooseRecipient = (recipient) => {
    const pending = askTo;
    setAskTo(null);
    mailRun(pending, recipient);
  };

  useEffect(() => {
    if (!askTo) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setAskTo(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [askTo]);

  const primary = callFirst
    ? { href: telHref(callPhone), label: personFirst ? `Call ${personFirst}` : "Call now", icon: "fa-phone", hint: callPhone }
    : email
      ? {
        onClick: () => mailRun({ kind: "ai_mail" }),
        label: personFirst ? `Write ${personFirst} with AI` : "Write mail with AI",
        icon: "fa-wand-magic-sparkles",
        hint: hasBrief ? "AI drafts it from the notes and this briefing" : "AI drafts it from the contact’s notes",
      }
      : { onClick: () => onRun({ kind: "open_notes" }), label: "Open notes", icon: "fa-sticky-note", hint: "No email on file yet" };

  const who = personFirst || company;
  const noMail = "No email address on file for this contact.";
  const tools = [
    callFirst && email && {
      id: "ai", label: "Write with AI", icon: "fa-wand-magic-sparkles", onClick: () => mailRun({ kind: "ai_mail" }),
      desc: "AI drafts the mail from the notes and this briefing. You check it before it goes out.",
    },
    {
      id: "template", label: "Use a template", icon: "fa-file-lines", onClick: () => toggle("template"), active: panel === "template",
      disabled: !email || !templates.length,
      desc: !email ? noMail : !templates.length ? "You have no email templates yet." : `Pick one of your templates. AI fills it in for ${who}, you review it before sending.`,
    },
    canInbox && {
      id: "scratch", label: "Write it yourself", icon: "fa-pen", onClick: () => toggle("scratch"), active: panel === "scratch",
      disabled: !email,
      desc: email ? "Opens the inbox with an empty mail, or as a reply in your last conversation." : noMail,
    },
    canInbox && {
      id: "forward", label: "Forward a conversation", icon: "fa-share", onClick: () => toggle("forward"), active: panel === "forward",
      disabled: !email,
      desc: email ? `Pass an earlier mail thread on to someone else at ${company}.` : noMail,
    },
    !callFirst && callPhone && {
      id: "call", label: "Call instead", icon: "fa-phone", href: telHref(callPhone),
      desc: `Dials ${callPhone}. Log the outcome in the notes afterwards.`,
    },
    {
      id: "notes", label: callFirst ? "Log the call" : "Open the notes", icon: "fa-sticky-note", onClick: () => onRun({ kind: "open_notes" }),
      desc: callFirst ? "Write down how the call went. It becomes the context for the next step." : `See everything about ${who} and write down what happened.`,
    },
    {
      id: "done", label: phase === "celebrate" ? "Done" : "Mark as done", icon: "fa-check", onClick: done, disabled: phase !== "idle",
      desc: link.kind === "fulfils"
        ? `Already handled outside the app? Close the task and ${link.flowName} moves on.`
        : "Already handled outside the app? Close the task without doing anything else.",
    },
  ].filter(Boolean);

  const templatePop = panel === "template" && (
    <div className="tp-pop" role="dialog" aria-label="Choose a template">
      <p>Choose a template</p>
      {templates.map(tpl => (
        <button key={tpl.id} type="button" onClick={() => { setPanel(null); mailRun({ kind: "template_mail", templateId: tpl.id }); }}>
          <i className="fa fa-file-lines"></i>
          <span>{tpl.topic}</span>
          <i className="fa fa-arrow-right"></i>
        </button>
      ))}
    </div>
  );
  const lastIn = mailList.find(m => m.dir === "in");
  const scratchPop = panel === "scratch" && (
    <div className="tp-pop tp-pop--choice" role="dialog" aria-label="How should the mail start?">
      <p>How should it start?</p>
      <button type="button" onClick={() => { setPanel(null); onRun({ kind: "new_mail", to: email }); }}>
        <i className="fa fa-file"></i>
        <span><strong>Blank mail</strong><small>New conversation to {email}</small></span>
        <i className="fa fa-arrow-right"></i>
      </button>
      <button
        type="button"
        disabled={!lastIn}
        onClick={() => { setPanel(null); onRun({ kind: "reply_thread", emailId: lastIn.id.replace(/^r_/, "") }); }}
      >
        <i className="fa fa-reply"></i>
        <span>
          <strong>With the previous mails</strong>
          <small>{lastIn ? `Reply in “${lastIn.subject}” · ${ago(lastIn.at)}` : mailsLoading ? "Looking for the thread…" : "No reply from them yet"}</small>
        </span>
        <i className="fa fa-arrow-right"></i>
      </button>
    </div>
  );

  const celebrating = phase === "celebrate";

  return (
    <article className={`tp-focus ${celebrating ? "is-done" : ""}`} aria-label={`Current task: ${headline}`}>
      {dmOpen && contact?.id && (
        <DecisionMakersModal
          isOpen
          contactData={contact}
          decisionMakers={dms}
          userId={worldId}
          onClose={() => setDmOpen(false)}
          onContactUpdate={(id, updates) => onContactPatch(id, updates)}
        />
      )}
      {askTo && createPortal(
        <div className="tp-ask-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setAskTo(null); }}>
          <div className="tp-ask" role="dialog" aria-modal="true" aria-labelledby="tp-ask-title">
            <span className="tp-ask-icon"><i className="fa fa-at"></i></span>
            <h3 id="tp-ask-title">
              {mentioned.length === 1 ? "Send it to the address from the note?" : "The note mentions other addresses"}
            </h3>
            <p>
              {mentioned.length === 1
                ? <>The note says to write to <b>{mentioned[0]}</b>. Should the mail go there?</>
                : <>Pick where the mail should go.</>}
            </p>
            <div className="tp-ask-actions">
              {mentioned.map(addr => (
                <button key={addr} type="button" className="altBtn altBtn--save" onClick={() => chooseRecipient(addr)}>
                  <i className="fa fa-check"></i>
                  {mentioned.length === 1 ? "Yes, send there" : addr}
                </button>
              ))}
              <button type="button" className="altBtn altBtn--petrol" onClick={() => chooseRecipient(null)}>
                {defaultTo ? <>No, keep {defaultTo}</> : "No, keep the usual address"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      <header className="tp-focus-head">
        <span className="tp-focus-icon" title={act?.label || "Task"}><i className={`fa ${act?.icon || "fa-bolt"}`}></i></span>
        <div className="tp-focus-title">
          <h2 className="tp-focus-headline">{headline}</h2>
          <p className="tp-focus-for">{recipient}{personName && <> · {company}</>}</p>
        </div>
        <div className="tp-focus-side">
          <span className={`tp-due ${due.tone}`}>
            {due.tone && <i className="fa fa-clock"></i>}
            {due.text}
          </span>
          {total > 1 && <small>{position} of {total}</small>}
        </div>
      </header>

      <div className="tp-focus-body">
        {(thread.length > 0 || hasBrief || mailsLoading) && (
          <ol className="tp-thread" ref={threadRef} aria-label={`History with ${company}`}>
            <li className="tp-thread-start">
              {mailsLoading && !thread.length
                ? "Loading history…"
                : history.length > thread.length
                  ? `Showing the last ${thread.length} of ${history.length} touchpoints`
                  : thread.length ? `Start of the history with ${company}` : `No touchpoints with ${company} yet`}
            </li>
            {thread.map(item => (
              <ThreadMessage key={item.id} item={item} contactInitials={contactInitials} label={label} />
            ))}
            {hasBrief && (
              <li className="tp-msg is-ours is-task">
                <MemberAvatar member={by} size={28} className="tp-chat-avatar" />
                <div className="tp-msg-bubble">
                  <span className="tp-msg-kind">
                    <i className={`fa ${act?.icon || "fa-bolt"}`}></i>
                    {selfTask ? `Your task · ${act?.label || "Task"}` : `${fromName} to ${toNames} · ${act?.label || "Task"}`}
                  </span>
                  <p className="tp-msg-text">{brief}</p>
                  {task.createdAt && <span className="tp-msg-meta">{formatDue(task.createdAt)}</span>}
                </div>
              </li>
            )}
          </ol>
        )}
        {!hasBrief && (
          <p className="tp-focus-by">
            {selfTask ? <>Noted by <b>{fromName}</b></> : <>From <b>{fromName}</b> to <b>{toNames}</b></>}
            {task.createdAt && <> · {ago(task.createdAt)}</>}
          </p>
        )}
      </div>

      <footer className="tp-focus-foot">
        <button type="button" className={`tp-context-toggle ${showContext ? "is-on" : ""}`} onClick={() => { setShowContext(v => !v); setShowWays(false); }} aria-expanded={showContext}>
          <i className="fa fa-building"></i>
          Flow & contact
          <i className="fa fa-chevron-down"></i>
        </button>
        <div className="tp-focus-cta">
          <button type="button" className={`tp-ways-toggle ${showWays ? "is-on" : ""}`} onClick={() => { setShowWays(v => !v); setShowContext(false); }} aria-expanded={showWays}>
            Do it another way <i className="fa fa-chevron-down"></i>
          </button>
          {primary.href
            ? <a className="altBtn altBtn--save tp-primary" href={primary.href} title={primary.hint}><i className={`fa ${primary.icon}`}></i>{primary.label}</a>
            : <button type="button" className="altBtn altBtn--save tp-primary" onClick={primary.onClick} title={primary.hint}><i className={`fa ${primary.icon}`}></i>{primary.label}</button>}
        </div>
      </footer>

      {showWays && (
        <div className="tp-ways">
          <div className="tp-tools">
            {tools.map(tool => {
              const body = (
                <>
                  <span className="tp-tool-icon"><i className={`fa ${tool.icon}`}></i></span>
                  <span className="tp-tool-text"><strong>{tool.label}</strong><small>{tool.desc}</small></span>
                </>
              );
              return (
                <span key={tool.id} className="tp-tool-slot">
                  {tool.href
                    ? <a className="tp-tool" href={tool.href}>{body}</a>
                    : (
                      <button type="button" className={`tp-tool ${tool.active ? "is-on" : ""}`} onClick={tool.onClick} disabled={tool.disabled}>
                        {body}
                      </button>
                    )}
                  {tool.id === "template" && templatePop}
                  {tool.id === "scratch" && scratchPop}
                </span>
              );
            })}
          </div>
          {panel === "forward" && (
            <div className="tp-sheet">
              <ForwardWizard worldId={worldId} contactEmail={email} colleagues={colleagues} templates={templates} onRun={onRun} />
            </div>
          )}
        </div>
      )}

      {showContext && (
        <div className="tp-context">
          <FlowStrip sourceNote={sourceNote} act={act} link={link} />
          <aside className="tp-block tp-contact">
            <div className="tp-block-head">
              <p className="tp-label"><i className="fa fa-building"></i> Contact</p>
              <button type="button" className="tp-link" onClick={() => setDmOpen(true)} disabled={!contact?.id}>
                <i className="fa fa-user-plus"></i> People
              </button>
            </div>
            <div className="tp-co">
              <h3>{company}</h3>
              {place && <p className="tp-place">{place}</p>}
            </div>
            {(status || replied) && (
              <div className="tp-badges">
                {status && <span className="tp-badge">{status}</span>}
                {replied && <span className="tp-badge tp-badge--good"><i className="fa fa-reply"></i> Replied</span>}
              </div>
            )}
            {dms.length > 1 && (
              <div className="tp-people" role="listbox" aria-label="Who to contact">
                {dms.map(dm => {
                  const key = dm.email || dm.name;
                  const on = (selectedDm?.email || selectedDm?.name) === key;
                  return (
                    <button key={key} type="button" role="option" aria-selected={on} className={on ? "is-on" : ""} onClick={() => setWhoKey(key)}>
                      <strong>{dm.name || nameFromEmail(dm.email) || "Unknown"}</strong>
                      <span>{dm.position || dm.role || "Decision maker"}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <ul className="tp-facts">
              {(personEmail || email) && (
                <li>
                  <i className="fa fa-envelope"></i>
                  <button type="button" title="Copy" onClick={() => copyKey("person", personEmail || email)}>{copied === "person" ? "Copied" : (personEmail || email)}</button>
                </li>
              )}
              {otherMail && (
                <li>
                  <i className="fa fa-at"></i>
                  <button type="button" title="Copy" onClick={() => copyKey("account", otherMail)}>{copied === "account" ? "Copied" : otherMail}</button>
                </li>
              )}
              {callPhone && <li><i className="fa fa-phone"></i><a href={telHref(callPhone)}>{callPhone}</a></li>}
              {website && <li><i className="fa fa-globe"></i><a href={linkOf(website)} target="_blank" rel="noreferrer">{website.replace(/^https?:\/\//, "")}</a></li>}
              {linkedin && <li><i className="fab fa-linkedin"></i><a href={linkOf(linkedin)} target="_blank" rel="noreferrer">LinkedIn</a></li>}
            </ul>
          </aside>
        </div>
      )}
    </article>
  );
};

const QueueRow = ({ task, contact, onPick }) => {
  const left = useCountdown(task.dueAt);
  const act = getTaskAction(task.action);
  const due = dueInfo(task.dueAt, left);
  const brief = (task.message || "").trim();
  const company = task.contactName || contact?.business_name || "Contact";
  return (
    <li>
      <button type="button" className="tp-row" onClick={onPick}>
        <span className="tp-row-icon"><i className={`fa ${act?.icon || "fa-bolt"}`}></i></span>
        <span className="tp-row-text">
          <strong>{brief && brief !== act?.label ? brief : act?.label || "Task"}</strong>
          <span>{act?.label || "Task"} · {company}</span>
        </span>
        <span className={`tp-due ${due.tone}`}>{due.text}</span>
      </button>
    </li>
  );
};

const ResultBanner = ({ result, onPlan, onClose }) => (
  <div className={`tp-result ${result.tone === "warn" ? "is-warn" : ""}`} role="status">
    <span className="tp-result-icon"><i className={`fa ${result.tone === "warn" ? "fa-circle-exclamation" : "fa-check"}`}></i></span>
    <p>
      <strong>{result.title}</strong>
      <span>{result.text}</span>
    </p>
    {result.plan && <button type="button" className="altBtn altBtn--petrol" onClick={onPlan}><i className="fa fa-plus"></i>Plan next step</button>}
    <button type="button" className="tp-result-close" onClick={onClose} aria-label="Dismiss"><i className="fa fa-xmark"></i></button>
  </div>
);

/** Card plus queue, painted only once contact and mail history are in. */
const TaskStack = ({
  listScope, rows, focusId, onFocus, contacts, flows, members, templates,
  worldId, canInbox, onRun, onDone, onContactPatch, onScopeReady,
}) => {
  const focus = rows.find(t => t.id === focusId) || rows[0];
  const rest = rows.filter(t => t.id !== focus.id);
  const contactPending = Boolean(focus.contactId) && !(focus.contactId in contacts);
  const contact = contactPending ? null : (contacts[focus.contactId] || null);
  const addresses = useMemo(
    () => (contactPending ? [] : contactMailAddresses(contact)),
    [contact, contactPending],
  );
  const { mails, loading: mailsLoading } = useContactMailHistory(
    !contactPending && canInbox ? worldId : null,
    contactPending ? null : focus.contactId,
    addresses,
  );
  const ready = !contactPending && !mailsLoading;
  const shownRef = useRef(null);
  const fresh = {
    focus,
    rest,
    contact,
    mails,
    position: rows.indexOf(focus) + 1,
    total: rows.length,
  };
  if (ready) shownRef.current = fresh;
  const previous = shownRef.current;
  const previousLive = previous && rows.some(t => t.id === previous.focus.id);
  const shown = ready ? fresh : (previousLive ? previous : null);
  const isShown = Boolean(shown);
  useLayoutEffect(() => {
    onScopeReady(listScope, isShown);
    return () => onScopeReady(listScope, false);
  }, [isShown, listScope, onScopeReady]);
  if (!shown) return null;

  const link = resolveTaskFlow(shown.focus, shown.contact, flows);
  return (
    <div className="tp-stack">
      <FocusCard
        key={shown.focus.id}
        worldId={worldId}
        task={shown.focus}
        contact={shown.contact}
        members={members}
        templates={templates}
        link={link}
        position={shown.position}
        total={shown.total}
        mails={shown.mails}
        mailsLoading={false}
        onRun={(shortcut) => onRun(shown.focus, shortcut)}
        onDone={() => onDone(shown.focus, link)}
        canInbox={canInbox}
        onContactPatch={onContactPatch}
      />
      {shown.rest.length > 0 && (
        <section className="tp-queue">
          <h2 className="tp-group-head">Up next<span>{shown.rest.length}</span></h2>
          <ol className="tp-queue-list">
            {shown.rest.map(task => (
              <QueueRow
                key={task.id}
                task={task}
                contact={contacts[task.contactId]}
                onPick={() => onFocus(task.id)}
              />
            ))}
          </ol>
        </section>
      )}
    </div>
  );
};

const TasksPage = () => {
  const navigate = useNavigate();
  const { userId: worldId, accountType, features = [] } = useActiveWorld();
  const [scope, setScope] = useState("mine");
  const mineQ = useOpenTasks(worldId, "mine");
  const byMeQ = useOpenTasks(worldId, "byMe");
  const lists = { mine: mineQ, byMe: byMeQ };
  const [members, setMembers] = useState([]);
  const [contacts, setContacts] = useState({});
  const [templates, setTemplates] = useState([]);
  const [flows, setFlows] = useState([]);
  const [focusIds, setFocusIds] = useState({});
  const [stackReady, setStackReady] = useState({ mine: false, byMe: false });
  const [result, setResult] = useState(null);
  const setScopeReady = useCallback((scopeId, ready) => {
    setStackReady(prev => (prev[scopeId] === ready ? prev : { ...prev, [scopeId]: ready }));
  }, []);
  const canInbox = accountType !== "member" || features.includes("all") || features.includes("inbox");
  const tabIndex = SCOPES.findIndex(s => s.id === scope);

  useEffect(() => {
    setStackReady({ mine: false, byMe: false });
    if (!worldId) return undefined;
    loadTeamMembers(worldId).then(setMembers).catch(err => console.error("Error loading team:", err));
    get(ref(database, `World/${worldId}/settings/customTopics`))
      .then(snap => {
        const data = snap.exists() ? snap.val() : {};
        setTemplates(Object.entries(data).map(([id, t]) => ({ id, topic: t?.topic || id })));
      })
      .catch(() => setTemplates([]));
    return onValue(
      ref(database, `World/${worldId}/settings/warmupFlows`),
      snap => setFlows(snap.exists() ? Object.entries(snap.val()).map(([id, f]) => ({ id, ...f })) : []),
      () => setFlows([]),
    );
  }, [worldId]);

  useEffect(() => {
    if (!result) return undefined;
    const t = window.setTimeout(() => setResult(null), RESULT_MS);
    return () => window.clearTimeout(t);
  }, [result]);

  const allTasks = [...mineQ.tasks, ...byMeQ.tasks];
  const contactIdsKey = [...new Set(allTasks.map(t => t.contactId).filter(Boolean))].sort().join(",");
  useEffect(() => {
    if (!worldId || !contactIdsKey) return undefined;
    let cancelled = false;
    const missing = contactIdsKey.split(",").filter(id => !(id in contacts));
    if (!missing.length) return undefined;
    Promise.all(missing.map(id => getDoc(doc(firestore, `World/${worldId}/db/${id}`))
      .then(s => [id, s.exists() ? { id, ...s.data() } : null])
      .catch(() => [id, null])))
      .then(entries => { if (!cancelled) setContacts(prev => ({ ...prev, ...Object.fromEntries(entries) })); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldId, contactIdsKey]);

  const run = (task, shortcut) => {
    const isInbox = ["new_mail", "reply_thread", "forward_thread"].includes(shortcut.kind);
    navigate(`/world?view=${isInbox ? "smartInbox" : "tableOne"}`);
    requestTaskAction({ ...shortcut, contactId: task.contactId });
  };

  const patchContact = (contactId, updates) => {
    setContacts(prev => ({ ...prev, [contactId]: { ...prev[contactId], ...updates } }));
    window.dispatchEvent(new CustomEvent(CONTACT_PATCH_EVENT, { detail: { contactId, updates } }));
  };

  const done = async (task, link) => {
    const company = task.contactName || contacts[task.contactId]?.business_name || "this contact";
    const act = getTaskAction(task.action);
    let flowResult = null;
    if (link.kind === "fulfils") {
      try {
        flowResult = await advanceLinkedFlow(worldId, task.contactId, link);
      } catch (err) {
        console.error("Error advancing flow:", err);
      }
      if (flowResult?.success) {
        patchContact(task.contactId, flowResult.isComplete
          ? { warmupFlow: null, flowEndStatus: flowResult.flowEndStatus || "completed" }
          : { warmupFlow: flowResult.newFlowState });
      }
    }

    const uids = await completeTask(worldId, task, link.kind === "fulfils" && flowResult?.success
      ? { flowStepDone: { flowId: link.flowId, stepIndex: link.stepIndex } }
      : {});
    if (uids) window.dispatchEvent(new CustomEvent(CONTACT_PATCH_EVENT, { detail: { contactId: task.contactId, updates: { open_task_assignees: uids } } }));
    const othersOpen = allTasks.some(t => t.id !== task.id && t.contactId === task.contactId);
    const title = `${act?.label || "Task"} done · ${company}`;

    if (link.kind === "fulfils" && !flowResult?.success) {
      setResult({ tone: "warn", title, text: `The flow “${link.flowName}” could not move on. Open it from the contact to continue.` });
    } else if (link.kind === "fulfils") {
      setResult({
        title,
        text: flowResult.isComplete
          ? `“${link.flowName}” is finished for this contact.`
          : `“${link.flowName}” moves on: ${link.after.label} ${inDays(link.after.delayDays)}.`,
        plan: flowResult.isComplete && !othersOpen,
        contactId: task.contactId,
      });
    } else if (link.kind === "extra") {
      setResult({ title, text: `“${link.flowName}” continues on its own with ${link.step.label}.`, contactId: task.contactId });
    } else {
      setResult({
        title,
        text: othersOpen ? "Another task for this contact is still open." : "No next step is planned for this contact yet.",
        plan: !othersOpen,
        contactId: task.contactId,
      });
    }
  };

  const planNext = () => {
    if (!result?.contactId) return;
    navigate("/world?view=tableOne");
    requestTaskAction({ kind: "open_notes", contactId: result.contactId });
    setResult(null);
  };

  const renderList = (listScope, listTasks, listLoading) => {
    const rows = sortTasks(listTasks);
    if (listLoading && !rows.length) return null;
    if (!rows.length) {
      return (
        <div className="tp-empty">
          <span className="tp-empty-icon"><i className="fa fa-check"></i></span>
          <h2>All clear</h2>
          <p>{listScope === "mine" ? "Nothing waiting. Assign the next step from a contact’s notes." : "Nothing open that you handed to others."}</p>
        </div>
      );
    }
    return (
      <TaskStack
        listScope={listScope}
        rows={rows}
        focusId={focusIds[listScope]}
        onScopeReady={setScopeReady}
        onFocus={(id) => {
          setFocusIds(prev => ({ ...prev, [listScope]: id }));
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        contacts={contacts}
        flows={flows}
        members={members}
        templates={templates}
        worldId={worldId}
        canInbox={canInbox}
        onRun={run}
        onDone={done}
        onContactPatch={patchContact}
      />
    );
  };

  const scoped = lists[scope].tasks;
  const openCount = scoped.length;
  const lateCount = scoped.filter(t => t.dueAt && t.dueAt < Date.now()).length;
  const loadingHead = (lists[scope].loading && !openCount) || (openCount > 0 && !stackReady[scope]);

  return (
    <div className="tp-page">
      <header className="tp-top">
        <div className="tp-title">
          <h1>
            {loadingHead ? "Tasks" : openCount === 0 ? "Nothing open" : (
              <>
                <span className="tp-title-n">{openCount}</span>
                {openCount === 1 ? " open task" : " open tasks"}
              </>
            )}
          </h1>
          {(loadingHead || lateCount > 0) && (
            <p>
              {loadingHead ? "Loading…" : (
                <span className="tp-title-late">{lateCount === 1 ? "1 is overdue" : `${lateCount} are overdue`}</span>
              )}
            </p>
          )}
        </div>
        <nav className="tp-switch" role="tablist" aria-label="Task scope">
          <span className="tp-switch-ink" style={{ transform: `translateX(${tabIndex * 100}%)` }} />
          {SCOPES.map(s => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={scope === s.id}
              className={`tp-tab ${scope === s.id ? "is-on" : ""}`}
              onClick={() => setScope(s.id)}
            >
              <i className={`fa ${s.icon}`} aria-hidden="true"></i>
              {s.label}
              {lists[s.id].tasks.length > 0 && <em>{lists[s.id].tasks.length}</em>}
            </button>
          ))}
        </nav>
      </header>

      {result && <ResultBanner result={result} onPlan={planNext} onClose={() => setResult(null)} />}

      {SCOPES.map(s => (
        <div key={s.id} className="tp-pane" hidden={scope !== s.id} role="tabpanel">
          {renderList(s.id, lists[s.id].tasks, lists[s.id].loading)}
        </div>
      ))}
    </div>
  );
};

export default TasksPage;
